"use client";

import { useEffect, useState, useCallback } from "react";
import { LayoutGrid, List } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

export type ViewMode = "grid" | "list";

const STORAGE_KEY = "rw-product-view";

export interface ViewToggleProps {
  className?: string;
  onChange?: (mode: ViewMode) => void;
}

export function ViewToggle({ className, onChange }: ViewToggleProps) {
  const [view, setView] = useState<ViewMode>("grid");
  const tc = useTranslations("common");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "grid" || saved === "list") {
        setView(saved);
        onChange?.(saved);
      }
    } catch {
      // localStorage not available
    }
  }, [onChange]);

  const handleChange = useCallback(
    (mode: ViewMode) => {
      setView(mode);
      onChange?.(mode);
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // localStorage not available
      }
    },
    [onChange]
  );

  return (
    <div
      className={cn(
        "flex items-center rounded-sm border border-brand-dark/15 overflow-hidden",
        className
      )}
    >
      <button
        onClick={() => handleChange("grid")}
        className={cn(
          "flex items-center justify-center p-1.5 transition-colors duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50",
          view === "grid"
            ? "bg-brand-dark text-white"
            : "bg-white text-brand-muted hover:text-brand-dark"
        )}
        aria-label={tc("gridView")}
        aria-pressed={view === "grid"}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        onClick={() => handleChange("list")}
        className={cn(
          "flex items-center justify-center p-1.5 transition-colors duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50",
          view === "list"
            ? "bg-brand-dark text-white"
            : "bg-white text-brand-muted hover:text-brand-dark"
        )}
        aria-label={tc("listView")}
        aria-pressed={view === "list"}
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useViewMode() {
  const [view, setView] = useState<ViewMode>("grid");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "grid" || saved === "list") {
        setView(saved);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  const handleChange = useCallback((mode: ViewMode) => {
    setView(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage not available
    }
  }, []);

  return { view, setView: handleChange };
}
